/**
 * Benchmark reuse across compatible invoice lines.
 *
 * A claim frequently bills the same service several times (two riggers, three
 * helpers, a second operator). The benchmark engine returns ONE market row per
 * service family, so every repeat occurrence used to render as "Requires
 * Additional Market Support" even though verified market evidence for that
 * exact service already exists inside the same claim.
 *
 * This module decides — deterministically, with no network and no pricing
 * invention — whether an unsupported invoice charge may borrow the market
 * RATE EVIDENCE proven for another charge in the same review.
 *
 * Invariants:
 *   • invoice lines are never merged; reuse attaches evidence to a row,
 *   • only the rate evidence travels (low / high / average) — expected amounts
 *     are always recomputed from the borrowing line's own quantity,
 *   • incompatible billing units, pricing bases or equipment subtypes block
 *     reuse and record the exact reason.
 */

import type { InventoryCharge } from "./charge-inventory";
import { normalizeChargeKey } from "./charge-inventory";

export type ReuseRejectionReason =
  | "different_service_family"
  | "billing_unit_mismatch"
  | "pricing_basis_mismatch"
  | "equipment_subtype_unverified"
  | "no_compatible_supported_line";

/** Rate evidence proven for a supported charge, eligible for reuse. */
export interface ReusableEvidence {
  /** The supported charge the evidence was proven on. */
  sourceChargeId: string;
  marketLow: number | null;
  marketHigh: number | null;
  marketAverage: number;
  sourceLabel: string;
  researchNote: string | null;
}

export interface ReuseCandidate {
  charge: InventoryCharge;
  evidence: ReusableEvidence;
}

export interface ReuseDecision {
  evidence: ReusableEvidence | null;
  reason: ReuseRejectionReason | null;
}

/**
 * Equipment / vehicle-class words. Detail text carrying one of these describes
 * the EQUIPMENT, not a person or an asset tag, and therefore participates in
 * compatibility. Anything else (employee names, unit numbers, OCR noise) is
 * irrelevant to the market rate and must not block reuse.
 */
const EQUIPMENT_WORD_RE =
  /(trailer|landoll|lowboy|rollback|flatbed|rotator|wrecker|van|tractor|dolly|crane|dump|loader|forklift|excavat|bobcat|skid|air\s?cushion|\d+\s?ton\b|heavy|medium|light)/i;

/** Pure asset tags ("Unit #010", "Truck 12") carry no rate meaning. */
const UNIT_TAG_RE = /^(unit|truck|trailer|veh|vehicle|asset|wrecker)\s*#?\s*[\w-]+$/i;

/**
 * Equipment-subtype signature for a charge, or null when the supporting detail
 * says nothing about the equipment class. Two charges may only share evidence
 * when their signatures agree — an unmatched signature means the rate impact
 * of that subtype is unverified, never that it is equivalent.
 */
export function equipmentSubtype(charge: InventoryCharge): string | null {
  const detail = (charge.detail ?? "").trim();
  if (!detail) return null;
  const parts = detail
    .split(/[\n,;/]+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .filter((p) => !UNIT_TAG_RE.test(p))
    .filter((p) => EQUIPMENT_WORD_RE.test(p));
  if (!parts.length) return null;
  const key = normalizeChargeKey(parts.join(" "));
  return key || null;
}

/** Billing unit implied by the invoice line (per-unit price basis). */
function pricingBasis(charge: InventoryCharge): number | null {
  return typeof charge.rate === "number" && Number.isFinite(charge.rate) ? charge.rate : null;
}

/**
 * Whether `target` may reuse the market evidence proven on `donor`.
 * Employee names, unit numbers and quantities are deliberately ignored.
 */
export function assessReuseCompatibility(
  target: InventoryCharge,
  donor: InventoryCharge,
): ReuseRejectionReason | null {
  if (!target.normalized || target.normalized !== donor.normalized) {
    return "different_service_family";
  }
  const targetBasis = pricingBasis(target);
  const donorBasis = pricingBasis(donor);
  if (targetBasis === null || donorBasis === null) return "billing_unit_mismatch";
  if (targetBasis !== donorBasis) return "pricing_basis_mismatch";
  if (equipmentSubtype(target) !== equipmentSubtype(donor)) {
    return "equipment_subtype_unverified";
  }
  return null;
}

/**
 * Picks the market evidence an unsupported charge may borrow from the
 * supported charges of the same review, or explains why none applies.
 */
export function resolveReusedEvidence(
  target: InventoryCharge,
  candidates: ReuseCandidate[],
): ReuseDecision {
  let bestReason: ReuseRejectionReason = "no_compatible_supported_line";
  for (const candidate of candidates) {
    if (candidate.charge.id === target.id) continue;
    const reason = assessReuseCompatibility(target, candidate.charge);
    if (reason === null) return { evidence: candidate.evidence, reason: null };
    if (reason !== "different_service_family") bestReason = reason;
  }
  return { evidence: null, reason: bestReason };
}
